/**
 * Volba lidra medzi zalozkami.
 *
 * PRECO TO VOBEC EXISTUJE:
 *   Prehliadac na HTTP/1.1 povoli najviac 6 sucasnych spojeni na jeden origin.
 *   API je iny origin nez SPA, takze SSE stream sutazi so VSETKYMI XHR na to
 *   iste API. Dve zalozky -> zostavaju 4 sloty; SEST zalozok -> uplny deadlock,
 *   ziadne API volanie uz nikdy nedobehne. Dispeceri bezne drzia viac zalozek
 *   a localhost:44350 je vzdy HTTP/1.1, takze sa to da reprodukovat aj v deve.
 *
 * RIESENIE:
 *   Jedna zalozka (lider) drzi jediny EventSource a udalosti rozposiela
 *   ostatnym cez BroadcastChannel. Lidrovstvo je lease v localStorage,
 *   obnovovany kazde 2 s a povazovany za mrtvy po 5 s.
 *
 * Ked BroadcastChannel chyba alebo localStorage hodi vynimku (Safari v
 * privatnom rezime), vrati sa spravanie do povodneho stavu: pripoji sa kazda
 * zalozka zvlast.
 *
 * LEASE aj KANAL su per prevadzka. localStorage a BroadcastChannel su
 * spolocne pre cely origin -- na jednej domene rozvozy.sk by inak lider
 * zalozky /veteran drzal stream Veteranu aj za zalozku /demo, ktora by tak
 * nedostala ziadne svoje udalosti.
 */
import { currentScope } from "./tenantStorage";

const LEASE_KEY = `rozvozy-rt-leader@${currentScope()}`;
const CHANNEL_NAME = `rozvozy-realtime@${currentScope()}`;
const OBNOVA_MS = 2000;
const MRTVY_PO_MS = 5000;
const KONTROLA_MS = 1000;
// Zapis -> kratka pauza -> opatovne precitanie. Lacne rozhodnutie remizy, ked
// sa o lease uchadzaju dve zalozky naraz.
const POTVRDENIE_MS = 50;

const mojeId =
  (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
  `t${Date.now()}${Math.random().toString(36).slice(2)}`;

let kanal = null;
let obnovaTimer = null;
let kontrolaTimer = null;
let somLider = false;
let bezi = false;
let handlery = {};

export function isSupported() {
  try {
    if (typeof BroadcastChannel === "undefined") return false;
    // Dotyk localStorage moze v privatnom rezime vyhodit vynimku.
    window.localStorage.setItem(`${LEASE_KEY}-probe`, "1");
    window.localStorage.removeItem(`${LEASE_KEY}-probe`);
    return true;
  } catch (err) {
    return false;
  }
}

export function isLeader() {
  return somLider;
}

function citajLease() {
  try {
    const raw = window.localStorage.getItem(LEASE_KEY);
    if (!raw) return null;
    const lease = JSON.parse(raw);
    if (!lease || typeof lease.ts !== "number") return null;
    return lease;
  } catch (err) {
    return null;
  }
}

function zapisLease() {
  try {
    window.localStorage.setItem(LEASE_KEY, JSON.stringify({ id: mojeId, ts: Date.now() }));
    return true;
  } catch (err) {
    return false;
  }
}

function uvolniLease() {
  try {
    const lease = citajLease();
    if (lease && lease.id === mojeId) window.localStorage.removeItem(LEASE_KEY);
  } catch (err) {
    // niet co uvolnovat
  }
}

function stanSaLidrom() {
  if (somLider) return;
  somLider = true;
  clearInterval(obnovaTimer);
  obnovaTimer = setInterval(() => {
    if (!somLider) return;
    const lease = citajLease();
    // Niekto iny lease prebral -- ustupujeme.
    if (lease && lease.id !== mojeId) {
      prestanBytLidrom();
      return;
    }
    zapisLease();
  }, OBNOVA_MS);
  handlery.onBecomeLeader?.();
}

function prestanBytLidrom() {
  if (!somLider) return;
  somLider = false;
  clearInterval(obnovaTimer);
  obnovaTimer = null;
  handlery.onBecomeFollower?.();
}

function skusPrevziat() {
  if (!bezi) return;

  const lease = citajLease();
  const volny = !lease || Date.now() - lease.ts > MRTVY_PO_MS;

  if (lease && lease.id === mojeId) {
    stanSaLidrom();
    return;
  }

  if (!volny) {
    if (somLider) prestanBytLidrom();
    return;
  }

  if (!zapisLease()) return;
  setTimeout(() => {
    if (!bezi) return;
    const potvrdenie = citajLease();
    if (potvrdenie && potvrdenie.id === mojeId) stanSaLidrom();
  }, POTVRDENIE_MS);
}

function onUnload() {
  // Bez toho by nova zalozka cakala az 5 s, kym lease vyprsi.
  uvolniLease();
}

/**
 * @param {{onBecomeLeader:Function, onBecomeFollower:Function, onMessage:Function}} cb
 */
export function start(cb) {
  if (bezi) return;
  handlery = cb || {};
  bezi = true;

  try {
    kanal = new BroadcastChannel(CHANNEL_NAME);
    kanal.onmessage = (ev) => handlery.onMessage?.(ev.data);
  } catch (err) {
    kanal = null;
  }

  window.addEventListener("beforeunload", onUnload);
  window.addEventListener("pagehide", onUnload);

  skusPrevziat();
  kontrolaTimer = setInterval(skusPrevziat, KONTROLA_MS);
}

export function stop() {
  if (!bezi) return;
  bezi = false;
  clearInterval(kontrolaTimer);
  clearInterval(obnovaTimer);
  kontrolaTimer = null;
  obnovaTimer = null;
  window.removeEventListener("beforeunload", onUnload);
  window.removeEventListener("pagehide", onUnload);
  uvolniLease();
  somLider = false;
  if (kanal) {
    try {
      kanal.close();
    } catch (err) {
      // uz je zatvoreny
    }
    kanal = null;
  }
  handlery = {};
}

/** Rozposle udalost ostatnym zalozkam. Vola iba lider. */
export function broadcast(sprava) {
  if (!kanal) return;
  try {
    kanal.postMessage(sprava);
  } catch (err) {
    // cross-tab fan-out je bonus, nie nutnost -- lider ma data tak ci tak
  }
}
